import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PopoverController } from '@ionic/angular';
import { filter } from 'rxjs/operators';
import { MobileMenuPopoverComponent } from '../mobile-menu-popover/mobile-menu-popover.component';

@Component({
  selector: 'app-mobile-nav',
  templateUrl: './mobile-nav.component.html',
  styleUrls: ['./mobile-nav.component.scss']
})
export class MobileNavComponent implements OnInit {
  public currentRoute: string = '/home';
  public isMobile: boolean = false;

  // Main navigation tabs for mobile
  public tabs = [
    {
      title: 'Home',
      url: '/home',
      icon: 'home'
    },
    {
      title: 'Food',
      url: '',
      icon: 'restaurant-outline',
      isFoodButton: true
    },
    {
      title: 'Map',
      url: '/psimap',
      icon: 'map'
    },
    {
      title: 'Menu',
      url: '',
      icon: 'menu',
      isMenuButton: true
    }
  ];

  public appPages: any[] = [];
  public isDarkmode: boolean = false;

  constructor(
    private router: Router,
    private deviceService: DeviceDetectorService,
    private popoverController: PopoverController
  ) {
    this.checkMobile();
    // Listen for window resize to update mobile detection
    window.addEventListener('resize', () => {
      this.checkMobile();
    });
  }

  // checks if device is mobile (responsive to window size)
  private checkMobile() {
    // Check both device type and window width
    const isMobileDevice = this.deviceService.isMobile();
    const isSmallScreen = window.innerWidth <= 768;
    this.isMobile = isMobileDevice || isSmallScreen;
  }

  ngOnInit() {
    // Track current route
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentRoute = event.url;
    });

    // Set initial route
    this.currentRoute = this.router.url;
    
    // Load menu pages and dark mode
    try {
      const storedMenu = localStorage.getItem('appMenu');
      if (storedMenu) {
        this.appPages = JSON.parse(storedMenu);
      }
      this.isDarkmode = JSON.parse(localStorage.getItem('darkMode') || 'false');
    } catch (e) {
      console.error('Error loading menu:', e);
    }
  }

  navigate(url: string) {
    if (url) {
      this.router.navigate([url]);
    }
  }

  async handleTabClick(tab: any, event: any) {
    if (tab.isMenuButton) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      await this.openMenu(event);
      return false;
    } else if (tab.isFoodButton) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      await this.openFoodMenu(event);
      return false;
    }
  }

  async openFoodMenu(event?: any) {
    try {
      // Always refresh menu pages from localStorage to ensure we have the latest
      try {
        const storedMenu = localStorage.getItem('appMenu');
        if (storedMenu) {
          this.appPages = JSON.parse(storedMenu);
        }
      } catch (e) {
        console.error('Error refreshing menu:', e);
      }
      
      // Find Food & Catering menu item
      const foodMenu = this.appPages.find((page: any) => page.title === 'Food & Catering');
      
      if (foodMenu && foodMenu.subPages && foodMenu.subPages.length > 0) {
        // Create a simple popover with food options
        const popover = await this.popoverController.create({
          component: MobileMenuPopoverComponent,
          showBackdrop: true,
          backdropDismiss: true,
          cssClass: 'mobile-menu-popover food-menu-popover',
          componentProps: {
            appPages: [foodMenu], // Only show food menu
            isDarkmode: this.isDarkmode,
            isFoodOnly: true
          },
          alignment: 'center',
          side: 'top',
          size: 'cover'
        });
        
        await popover.present();
        
        // Position the popover
        const centerPopover = () => {
          const popoverContent = document.querySelector('.food-menu-popover .popover-content') as HTMLElement;
          const popoverElement = document.querySelector('ion-popover.food-menu-popover') as HTMLElement;
          const viewportWidth = window.innerWidth;
          
          if (popoverElement) {
            popoverElement.style.setProperty('--width', '100%', 'important');
            popoverElement.style.setProperty('--max-width', '100%', 'important');
            popoverElement.style.setProperty('--min-width', '100%', 'important');
          }
          
          if (popoverContent) {
            const navbarHeight = 64;
            const safeArea = parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)') || '0');
            const bottomPosition = navbarHeight + safeArea;
            
            popoverContent.style.setProperty('width', `${viewportWidth}px`, 'important');
            popoverContent.style.setProperty('max-width', '100%', 'important');
            popoverContent.style.setProperty('min-width', '100%', 'important');
            popoverContent.style.setProperty('left', '50%', 'important');
            popoverContent.style.setProperty('right', 'auto', 'important');
            popoverContent.style.setProperty('top', 'auto', 'important');
            popoverContent.style.setProperty('bottom', `${bottomPosition}px`, 'important');
            popoverContent.style.setProperty('transform', 'translateX(-50%)', 'important');
            popoverContent.style.setProperty('position', 'fixed', 'important');
            popoverContent.style.setProperty('z-index', '10001', 'important');
            popoverContent.style.setProperty('display', 'block', 'important');
            popoverContent.style.setProperty('visibility', 'visible', 'important');
            popoverContent.style.setProperty('opacity', '1', 'important');
            popoverContent.style.setProperty('margin', '0', 'important');
            popoverContent.style.setProperty('padding', '0', 'important');
          }
        };
        
        centerPopover();
        setTimeout(centerPopover, 50);
        setTimeout(centerPopover, 150);
        setTimeout(centerPopover, 300);
      } else {
        // If no food menu found, try to create a default one or open full menu
        console.warn('Food menu not found, opening full menu');
        await this.openMenu(event);
      }
    } catch (error) {
      console.error('Error opening food menu:', error);
      // Fallback: open full menu
      await this.openMenu(event);
    }
  }

  async openMenu(event?: any) {
    try {
      // Always refresh menu pages from localStorage to ensure we have the latest
      try {
        const storedMenu = localStorage.getItem('appMenu');
        if (storedMenu) {
          this.appPages = JSON.parse(storedMenu);
        }
      } catch (e) {
        console.error('Error refreshing menu:', e);
      }
      
      const popover = await this.popoverController.create({
        component: MobileMenuPopoverComponent,
        showBackdrop: true,
        backdropDismiss: true,
        cssClass: 'mobile-menu-popover',
        componentProps: {
          appPages: this.appPages,
          isDarkmode: this.isDarkmode,
          isFoodOnly: false
        },
        alignment: 'center',
        side: 'top',
        size: 'cover'
      });
      
      await popover.present();
      
      // Center the popover on screen with 100% width - try multiple times to ensure it works
      const centerPopover = () => {
        const popoverContent = document.querySelector('.mobile-menu-popover .popover-content') as HTMLElement;
        const popoverElement = document.querySelector('ion-popover.mobile-menu-popover') as HTMLElement;
        const viewportWidth = window.innerWidth;
        
        if (popoverElement) {
          popoverElement.style.setProperty('--width', '100%', 'important');
          popoverElement.style.setProperty('--max-width', '100%', 'important');
          popoverElement.style.setProperty('--min-width', '100%', 'important');
        }
        
        if (popoverContent) {
          const navbarHeight = 64;
          const safeArea = parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)') || '0');
          const bottomPosition = navbarHeight + safeArea;
          
          popoverContent.style.setProperty('width', `${viewportWidth}px`, 'important');
          popoverContent.style.setProperty('max-width', '100%', 'important');
          popoverContent.style.setProperty('min-width', '100%', 'important');
          popoverContent.style.setProperty('left', '50%', 'important');
          popoverContent.style.setProperty('right', 'auto', 'important');
          popoverContent.style.setProperty('top', 'auto', 'important');
          popoverContent.style.setProperty('bottom', `${bottomPosition}px`, 'important');
          popoverContent.style.setProperty('transform', 'translateX(-50%)', 'important');
          popoverContent.style.setProperty('position', 'fixed', 'important');
          popoverContent.style.setProperty('z-index', '10001', 'important');
          popoverContent.style.setProperty('display', 'block', 'important');
          popoverContent.style.setProperty('visibility', 'visible', 'important');
          popoverContent.style.setProperty('opacity', '1', 'important');
          popoverContent.style.setProperty('margin', '0', 'important');
          popoverContent.style.setProperty('padding', '0', 'important');
        }
      };
      
      // Try immediately and after a delay
      centerPopover();
      setTimeout(centerPopover, 50);
      setTimeout(centerPopover, 150);
      setTimeout(centerPopover, 300);
    } catch (error) {
      console.error('Error opening menu:', error);
    }
  }

  isActive(url: string): boolean {
    if (!url) return false;
    return this.currentRoute === url || this.currentRoute.startsWith(url + '/');
  }
}

